/**
 * ContractEditorSheet — création/édition contrat (S2).
 * Party polymorphe : cherche dans /clients ou /suppliers selon party_type.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
SheetDescription, } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Save, FileSignature, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  party_type: "client", party_id: "", party_name: "",
  title: "", contract_type: "prestation", reference: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "", amount_ht: 0, vat_rate: 20, billing_cycle: "monthly",
  tacit_renewal: true, tacit_renewal_period_months: 12,
  cancellation_notice_days: 30, status: "active", notes: "",
};

export default function ContractEditorSheet({ contractId, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [parties, setParties] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!contractId;

  useEffect(() => {
    if (!open) return;
    (async () => {
      const collection = form.party_type === "client" ? "/clients" : "/suppliers";
      try {
        const { data } = await api.get(collection);
        setParties(data.clients || data.suppliers || []);
      } catch { /* silent */ }
    })();
  }, [open, form.party_type]);

  useEffect(() => {
    if (!open) return;
    if (!contractId) { setForm(EMPTY); return; }
    (async () => {
      try {
        const { data } = await api.get(`/contracts/${contractId}`);
        setForm({ ...EMPTY, ...data.contract });
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [contractId, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setParty = (id) => {
    const p = parties.find((x) => x.id === id);
    setForm((f) => ({ ...f, party_id: id, party_name: p?.name || "" }));
  };

  const save = async () => {
    if (!form.title || !form.party_id || !form.start_date) {
      toast.error("Titre, tiers et date de début obligatoires."); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, end_date: form.end_date || null };
      if (isEdit) {
        await api.patch(`/contracts/${contractId}`, payload);
      } else {
        await api.post("/contracts", payload);
      }
      toast.success(isEdit ? "Contrat modifié" : "Contrat créé");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!isEdit || !window.confirm("Résilier ce contrat ?")) return;
    try {
      await api.delete(`/contracts/${contractId}`);
      toast.success("Contrat résilié");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-blue-600" />
            {isEdit ? "Modifier contrat" : "Nouveau contrat"}
          </SheetTitle><SheetDescription className="sr-only">Détails du panneau latéral.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="flex-1 px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Type de tiers</Label>
              <Select value={form.party_type} onValueChange={(v) => set("party_type", v)}>
                <SelectTrigger className="mt-1" data-testid="contract-party-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="supplier">Fournisseur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Tiers *</Label>
              <Select value={form.party_id} onValueChange={setParty}>
                <SelectTrigger className="mt-1" data-testid="contract-party">
                  <SelectValue placeholder="Sélectionner…" />
                </SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Titre *</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)}
                    className="mt-1" placeholder="Ex : Contrat de maintenance SaaS"
                    data-testid="contract-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Référence</Label>
              <Input value={form.reference || ""} onChange={(e) => set("reference", e.target.value)}
                      className="mt-1 font-mono" placeholder="CTR-2026-001" />
            </div>
            <div><Label className="text-xs">Type</Label>
              <Select value={form.contract_type} onValueChange={(v) => set("contract_type", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prestation">Prestation</SelectItem>
                  <SelectItem value="licence">Licence SaaS</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="assurance">Assurance</SelectItem>
                  <SelectItem value="loyer">Loyer</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date de début *</Label>
              <div className="mt-1"><DatePickerFR value={form.start_date}
                onChange={(v) => set("start_date", v)} /></div>
            </div>
            <div><Label className="text-xs">Date de fin</Label>
              <div className="mt-1"><DatePickerFR value={form.end_date}
                onChange={(v) => set("end_date", v)} /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Montant HT</Label>
              <Input type="number" value={form.amount_ht}
                      onChange={(e) => set("amount_ht", Number(e.target.value))}
                      className="mt-1 font-mono" data-testid="contract-amount" />
            </div>
            <div><Label className="text-xs">Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => set("billing_cycle", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_shot">Ponctuel</SelectItem>
                  <SelectItem value="monthly">Mensuel</SelectItem>
                  <SelectItem value="quarterly">Trimestriel</SelectItem>
                  <SelectItem value="semi_annual">Semestriel</SelectItem>
                  <SelectItem value="annual">Annuel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Reconduction tacite</Label>
              <Switch checked={form.tacit_renewal}
                       onCheckedChange={(v) => set("tacit_renewal", v)}
                       data-testid="contract-tacit-renewal" />
            </div>
            {form.tacit_renewal && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px]">Période (mois)</Label>
                  <Input type="number" value={form.tacit_renewal_period_months}
                          onChange={(e) => set("tacit_renewal_period_months", Number(e.target.value))}
                          className="mt-1 font-mono h-8" />
                </div>
                <div><Label className="text-[10px]">Préavis (jours)</Label>
                  <Input type="number" value={form.cancellation_notice_days}
                          onChange={(e) => set("cancellation_notice_days", Number(e.target.value))}
                          className="mt-1 font-mono h-8" />
                </div>
              </div>
            )}
          </div>
          <div><Label className="text-xs">Notes</Label>
            <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
                       className="mt-1 min-h-[80px]" />
          </div>
        </ScrollArea>
        <div className="border-t border-slate-200 px-6 py-3 shrink-0 flex justify-between gap-2">
          {isEdit && (
            <Button variant="outline" onClick={remove}
                     className="border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1.5" />Résilier
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="contract-save-btn">
              <Save className="h-4 w-4 mr-1.5" />{saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
